// Admin "Portal Access" tab — Phase 3 + Phase 4.
// Per-client view of every portal user (advisers + principals) with granular
// visibility / permission toggles, expiry control, custom welcome notes, audit
// trail viewer, and two-way action triggers (new action item + document request).
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  ShieldCheck, Mail, Clock, History, ChevronDown, ChevronRight, Sparkles,
  PencilLine, Trash2, FilePlus2, ListChecks, Loader2, AlertTriangle, RefreshCw,
  BellRing,
} from 'lucide-react';
import {
  portalAdminApi, PRINCIPAL_SECTIONS, ADVISER_SECTIONS, PERMISSION_KEYS,
} from '@/lib/portalAdminApi';

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function fmtDate(iso, fmt = 'd MMM yyyy') {
  if (!iso) return '—';
  try { return format(parseISO(iso), fmt); } catch { return iso; }
}

function isOverdue(item) {
  if (!item?.dueDate || item.status === 'completed' || item.status === 'fulfilled' || item.status === 'cancelled') return false;
  try { return parseISO(item.dueDate) < new Date(); } catch { return false; }
}

function toDateInputValue(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'yyyy-MM-dd'); } catch { return ''; }
}

function PriorityBadge({ priority }) {
  const map = {
    urgent: 'bg-red-50 text-red-700 border-red-200',
    high: 'bg-amber-50 text-amber-700 border-amber-200',
    normal: 'bg-[#F0EBE6] text-[#4A3B32] border-[#E6E0D9]',
    low: 'bg-slate-50 text-slate-600 border-slate-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] capitalize ${map[priority] || map.normal}`}>
      {priority || 'normal'}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: 'bg-[#FFF8F0] text-[#A86A3A] border-[#E6D9C4]',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    fulfilled: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] capitalize ${map[status] || map.open}`}>
      {status || 'open'}
    </Badge>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────

export default function ClientPortalAccess() {
  const { clientId, client } = useOutletContext();
  const { token } = useAuth();

  const [users, setUsers] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);

  // Dialog states
  const [createItemFor, setCreateItemFor] = useState(null);   // portal user object
  const [createDocFor, setCreateDocFor] = useState(null);
  const [editUser, setEditUser] = useState(null);             // for welcome note / matter scope dialog

  const loadAll = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    try {
      const [u, a, d, au] = await Promise.all([
        portalAdminApi.listPortalUsers(token, clientId),
        portalAdminApi.listActionItems(token, clientId),
        portalAdminApi.listDocumentRequests(token, clientId),
        portalAdminApi.auditLog(token, clientId, { limit: 50 }),
      ]);
      setUsers(u.data?.users || []);
      setActionItems(a.data?.items || []);
      setDocRequests(d.data?.items || []);
      setAuditEvents(au.data?.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load portal access');
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="portal-access-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-access-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-[#A86A3A]" />
            <h1 className="font-heading text-3xl text-[#2C2420]">Portal Access</h1>
          </div>
          <p className="text-sm text-[#5D524D]">
            Manage exactly what each portal user can see and do for <strong>{client?.name}</strong>.
          </p>
        </div>
        <Button onClick={loadAll} variant="outline" size="sm" className="gap-2" data-testid="portal-access-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </header>

      {users.length === 0 ? (
        <EmptyState clientName={client?.name} />
      ) : (
        <>
          {/* Portal users list */}
          <section className="space-y-3" data-testid="portal-users-list">
            {users.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                expanded={expandedUser === u.id}
                onToggle={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                token={token}
                clientId={clientId}
                onUpdate={loadAll}
                onCreateAction={() => setCreateItemFor(u)}
                onRequestDoc={() => setCreateDocFor(u)}
                onEditMeta={() => setEditUser(u)}
                actionItems={actionItems.filter((i) => i.portalUserId === u.id)}
                docRequests={docRequests.filter((i) => i.portalUserId === u.id)}
              />
            ))}
          </section>

          {/* All action items + doc requests (cross-user) */}
          <Tabs defaultValue="actions" className="bg-white rounded-2xl border border-[#E6E0D9] p-5" data-testid="portal-access-workflows">
            <TabsList className="bg-[#F9F7F5] mb-4">
              <TabsTrigger value="actions" data-testid="tab-action-items">
                <ListChecks className="w-3.5 h-3.5 mr-1.5" /> All action items ({actionItems.length})
              </TabsTrigger>
              <TabsTrigger value="docs" data-testid="tab-doc-requests">
                <FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> Document requests ({docRequests.length})
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">
                <History className="w-3.5 h-3.5 mr-1.5" /> Audit ({auditEvents.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="actions">
              <ActionItemsTable
                items={actionItems}
                userMap={userMap}
                token={token}
                onUpdate={loadAll}
              />
            </TabsContent>
            <TabsContent value="docs">
              <DocumentRequestsTable
                items={docRequests}
                userMap={userMap}
                token={token}
                onUpdate={loadAll}
              />
            </TabsContent>
            <TabsContent value="audit">
              <AuditTable events={auditEvents} />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Dialogs */}
      {createItemFor && (
        <CreateActionItemDialog
          user={createItemFor}
          token={token}
          clientId={clientId}
          onClose={() => setCreateItemFor(null)}
          onCreated={loadAll}
        />
      )}
      {createDocFor && (
        <CreateDocumentRequestDialog
          user={createDocFor}
          token={token}
          clientId={clientId}
          onClose={() => setCreateDocFor(null)}
          onCreated={loadAll}
        />
      )}
      {editUser && (
        <EditUserMetaDialog
          user={editUser}
          token={token}
          onClose={() => setEditUser(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}

function EmptyState({ clientName }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#E6E0D9] bg-white p-12 text-center" data-testid="portal-access-empty">
      <ShieldCheck className="w-10 h-10 text-[#8C8078] mx-auto mb-3" />
      <h3 className="font-medium text-[#2C2420] mb-1">No portal users yet</h3>
      <p className="text-sm text-[#8C8078] max-w-md mx-auto">
        Once you invite a Family Principal or grant an External Adviser access, they'll appear here so you can
        fine-tune what they see for <strong>{clientName || 'this client'}</strong>.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// User card — collapses into visibility / permission / expiry / actions panels
// ──────────────────────────────────────────────────────────────────────────

function UserCard({
  user, expanded, onToggle, token, clientId, onUpdate,
  onCreateAction, onRequestDoc, onEditMeta, actionItems, docRequests,
}) {
  const isAdviser = user.portalRole === 'external_adviser';
  const sections = isAdviser ? ADVISER_SECTIONS : PRINCIPAL_SECTIONS;
  const openActions = actionItems.filter((i) => i.status === 'open').length;
  const openDocs = docRequests.filter((i) => i.status === 'open').length;
  const overdueCount = [...actionItems, ...docRequests].filter(isOverdue).length;
  const expired = user.expiresAt && new Date(user.expiresAt) < new Date();

  return (
    <article className="bg-white rounded-2xl border border-[#E6E0D9] overflow-hidden" data-testid={`portal-user-card-${user.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-[#F9F7F5]/60 transition-colors"
        data-testid={`portal-user-toggle-${user.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${isAdviser ? 'bg-[#F0EBE6] text-[#4A3B32]' : 'bg-[#FFF8F0] text-[#A86A3A]'}`}>
            {(user.name || '?').charAt(0)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-[#2C2420] truncate">{user.name}</h3>
              <Badge variant="outline" className="text-[10px]">
                {isAdviser ? 'External Adviser' : 'Family Principal'}
              </Badge>
              {!user.active && <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500">Revoked</Badge>}
              {expired && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Expired</Badge>}
              {overdueCount > 0 && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`portal-user-overdue-pill-${user.id}`}>
                  <BellRing className="w-2.5 h-2.5 mr-0.5" /> {overdueCount} overdue
                </Badge>
              )}
            </div>
            <p className="text-xs text-[#8C8078] truncate flex items-center gap-2 mt-0.5">
              <Mail className="w-3 h-3" /> {user.email}
              {user.lastLoginAt && <>· last sign-in {fmtDate(user.lastLoginAt, "d MMM 'at' h:mm a")}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {(openActions > 0 || openDocs > 0) && (
            <span className="text-xs text-[#A86A3A] hidden sm:inline">
              {openActions > 0 && `${openActions} action${openActions === 1 ? '' : 's'}`}
              {openActions > 0 && openDocs > 0 && ' · '}
              {openDocs > 0 && `${openDocs} doc${openDocs === 1 ? '' : 's'}`}
            </span>
          )}
          {expanded ? <ChevronDown className="w-4 h-4 text-[#8C8078]" /> : <ChevronRight className="w-4 h-4 text-[#8C8078]" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#F0EAE3] p-5 space-y-6 bg-[#FBFAF8]" data-testid={`portal-user-panel-${user.id}`}>
          {/* Two-way action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={onCreateAction} className="bg-[#4A3B32] hover:bg-[#3A2E26] gap-1.5" data-testid={`portal-new-action-btn-${user.id}`}>
              <ListChecks className="w-3.5 h-3.5" /> New action item
            </Button>
            <Button size="sm" variant="outline" onClick={onRequestDoc} className="gap-1.5" data-testid={`portal-request-doc-btn-${user.id}`}>
              <FilePlus2 className="w-3.5 h-3.5" /> Request a document
            </Button>
            <Button size="sm" variant="outline" onClick={onEditMeta} className="gap-1.5" data-testid={`portal-edit-meta-btn-${user.id}`}>
              <PencilLine className="w-3.5 h-3.5" /> Welcome note / scope
            </Button>
          </div>

          {/* Visibility & permissions grid */}
          <div className="grid lg:grid-cols-2 gap-4">
            <VisibilityPanel user={user} sections={sections} token={token} onUpdate={onUpdate} />
            <div className="space-y-4">
              <PermissionsPanel user={user} token={token} onUpdate={onUpdate} />
              <ExpiryPanel user={user} token={token} onUpdate={onUpdate} />
              <RevokePanel user={user} token={token} onUpdate={onUpdate} />
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Visibility / Permission / Expiry panels (in-line)
// ──────────────────────────────────────────────────────────────────────────

function VisibilityPanel({ user, sections, token, onUpdate }) {
  const [saving, setSaving] = useState(null);
  const toggle = async (key, value) => {
    setSaving(key);
    try {
      await portalAdminApi.patchPortalUser(token, user.id, { visibility: { ...user.visibility, [key]: value } });
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update visibility');
    } finally {
      setSaving(null);
    }
  };
  return (
    <section className="bg-white rounded-xl border border-[#E6E0D9] p-4">
      <h4 className="text-sm font-medium text-[#2C2420] mb-3 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-[#A86A3A]" /> What they can see
      </h4>
      <ul className="space-y-2.5" data-testid={`portal-visibility-list-${user.id}`}>
        {sections.map((s) => {
          const checked = user.visibility?.[s.key] !== false;
          return (
            <li key={s.key} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-[#2C2420] font-medium">{s.label}</p>
                <p className="text-xs text-[#8C8078] leading-snug">{s.description}</p>
              </div>
              <Switch
                checked={checked}
                disabled={saving === s.key}
                onCheckedChange={(v) => toggle(s.key, v)}
                data-testid={`portal-visibility-switch-${user.id}-${s.key}`}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PermissionsPanel({ user, token, onUpdate }) {
  const [saving, setSaving] = useState(null);
  const toggle = async (key, value) => {
    setSaving(key);
    try {
      await portalAdminApi.patchPortalUser(token, user.id, { permissions: { ...user.permissions, [key]: value } });
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update permission');
    } finally {
      setSaving(null);
    }
  };
  return (
    <section className="bg-white rounded-xl border border-[#E6E0D9] p-4">
      <h4 className="text-sm font-medium text-[#2C2420] mb-3">What they can do</h4>
      <ul className="space-y-2.5" data-testid={`portal-permissions-list-${user.id}`}>
        {PERMISSION_KEYS.map((p) => {
          const checked = user.permissions?.[p.key] !== false;
          return (
            <li key={p.key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-[#2C2420]">{p.label}</span>
              <Switch
                checked={checked}
                disabled={saving === p.key}
                onCheckedChange={(v) => toggle(p.key, v)}
                data-testid={`portal-permission-switch-${user.id}-${p.key}`}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ExpiryPanel({ user, token, onUpdate }) {
  const [value, setValue] = useState(toDateInputValue(user.expiresAt));
  const [saving, setSaving] = useState(false);
  const save = async (iso) => {
    setSaving(true);
    try {
      await portalAdminApi.patchPortalUser(token, user.id, { expiresAt: iso });
      toast.success(iso ? 'Expiry updated' : 'Expiry cleared');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update expiry');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="bg-white rounded-xl border border-[#E6E0D9] p-4">
      <h4 className="text-sm font-medium text-[#2C2420] mb-3 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-[#A86A3A]" /> Access expiry
      </h4>
      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="text-sm"
          data-testid={`portal-expiry-input-${user.id}`}
        />
        <Button
          size="sm"
          onClick={() => save(value ? new Date(`${value}T00:00:00Z`).toISOString() : '')}
          disabled={saving}
          data-testid={`portal-expiry-save-${user.id}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
        </Button>
        {value && (
          <Button size="sm" variant="ghost" onClick={() => { setValue(''); save(''); }} data-testid={`portal-expiry-clear-${user.id}`}>
            Clear
          </Button>
        )}
      </div>
      <p className="text-xs text-[#8C8078] mt-2">
        After this date, the portal user can no longer sign in. Leave blank for indefinite access.
      </p>
    </section>
  );
}

function RevokePanel({ user, token, onUpdate }) {
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const isRevoked = !user.active;

  const flip = async () => {
    setSaving(true);
    try {
      await portalAdminApi.patchPortalUser(token, user.id, { active: isRevoked });
      toast.success(isRevoked ? 'Access restored' : 'Access revoked');
      setConfirming(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update access');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`rounded-xl border p-4 ${isRevoked ? 'bg-amber-50/40 border-amber-200' : 'bg-red-50/30 border-red-100'}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${isRevoked ? 'text-amber-700' : 'text-red-600'}`} />
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-[#2C2420]">{isRevoked ? 'Access is revoked' : 'Revoke portal access'}</h4>
          <p className="text-xs text-[#5D524D] leading-snug mb-3">
            {isRevoked
              ? 'Restore to allow this user to sign in again.'
              : 'Sign-ins blocked immediately. They keep their account but can\'t access any portal data.'}
          </p>
          {confirming ? (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="destructive" onClick={flip} disabled={saving} data-testid={`portal-revoke-confirm-${user.id}`}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isRevoked ? 'Yes, restore' : 'Yes, revoke')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant={isRevoked ? 'outline' : 'destructive'}
              onClick={() => setConfirming(true)}
              data-testid={`portal-revoke-btn-${user.id}`}
            >
              {isRevoked ? 'Restore access' : 'Revoke access'}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Action items / Doc requests tables
// ──────────────────────────────────────────────────────────────────────────

function ActionItemsTable({ items, userMap, token, onUpdate }) {
  if (items.length === 0) {
    return <p className="text-sm text-[#8C8078] text-center py-8">No action items yet. Open a user and click "New action item" to send one.</p>;
  }
  return (
    <table className="w-full text-sm" data-testid="portal-action-items-table">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-[#8C8078]">
          <th className="pb-2 font-medium">Title</th>
          <th className="pb-2 font-medium">Recipient</th>
          <th className="pb-2 font-medium">Due</th>
          <th className="pb-2 font-medium">Priority</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 font-medium text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#F0EAE3]">
        {items.map((i) => {
          const overdue = isOverdue(i);
          return (
            <tr key={i.id} data-testid={`portal-action-row-${i.id}`}>
              <td className="py-3 pr-3 align-top">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[#2C2420] font-medium">{i.title}</p>
                  {overdue && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`overdue-badge-${i.id}`}>
                      Overdue
                    </Badge>
                  )}
                </div>
                {i.description && <p className="text-xs text-[#8C8078] mt-0.5 line-clamp-2">{i.description}</p>}
                {i.lastNudgedAt && (
                  <p className="text-[10px] text-[#A86A3A] mt-1">Nudged {fmtDate(i.lastNudgedAt, "d MMM 'at' h:mm a")}{i.nudgeCount > 1 ? ` · ${i.nudgeCount} times` : ''}</p>
                )}
              </td>
              <td className="py-3 pr-3 align-top text-[#5D524D]">{userMap[i.portalUserId]?.name || i.portalUserName || '—'}</td>
              <td className="py-3 pr-3 align-top text-[#5D524D]">{fmtDate(i.dueDate)}</td>
              <td className="py-3 pr-3 align-top"><PriorityBadge priority={i.priority} /></td>
              <td className="py-3 pr-3 align-top"><StatusBadge status={i.status} /></td>
              <td className="py-3 align-top text-right">
                <RowActions
                  onNudge={overdue ? async () => {
                    await portalAdminApi.nudgeActionItem(token, i.id);
                    toast.success('Reminder sent');
                    onUpdate();
                  } : null}
                  onComplete={i.status !== 'completed' ? async () => {
                    await portalAdminApi.patchActionItem(token, i.id, { status: 'completed' });
                    toast.success('Marked complete');
                    onUpdate();
                  } : null}
                  onReopen={i.status === 'completed' ? async () => {
                    await portalAdminApi.patchActionItem(token, i.id, { status: 'open' });
                    onUpdate();
                  } : null}
                  onDelete={async () => {
                    await portalAdminApi.deleteActionItem(token, i.id);
                    toast.success('Removed');
                    onUpdate();
                  }}
                  testIdSuffix={`action-${i.id}`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DocumentRequestsTable({ items, userMap, token, onUpdate }) {
  if (items.length === 0) {
    return <p className="text-sm text-[#8C8078] text-center py-8">No document requests open. Open a user and click "Request a document".</p>;
  }
  return (
    <table className="w-full text-sm" data-testid="portal-doc-requests-table">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-[#8C8078]">
          <th className="pb-2 font-medium">Document</th>
          <th className="pb-2 font-medium">Recipient</th>
          <th className="pb-2 font-medium">Due</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 font-medium text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#F0EAE3]">
        {items.map((r) => {
          const overdue = isOverdue(r);
          return (
            <tr key={r.id} data-testid={`portal-doc-row-${r.id}`}>
              <td className="py-3 pr-3 align-top">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[#2C2420] font-medium">{r.title}</p>
                  {overdue && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`overdue-badge-${r.id}`}>
                      Overdue
                    </Badge>
                  )}
                </div>
                {r.description && <p className="text-xs text-[#8C8078] mt-0.5 line-clamp-2">{r.description}</p>}
                {r.lastNudgedAt && (
                  <p className="text-[10px] text-[#A86A3A] mt-1">Nudged {fmtDate(r.lastNudgedAt, "d MMM 'at' h:mm a")}{r.nudgeCount > 1 ? ` · ${r.nudgeCount} times` : ''}</p>
                )}
              </td>
              <td className="py-3 pr-3 align-top text-[#5D524D]">{userMap[r.portalUserId]?.name || r.portalUserName || '—'}</td>
              <td className="py-3 pr-3 align-top text-[#5D524D]">{fmtDate(r.dueDate)}</td>
              <td className="py-3 pr-3 align-top"><StatusBadge status={r.status} /></td>
              <td className="py-3 align-top text-right">
                <RowActions
                  onNudge={overdue ? async () => {
                    await portalAdminApi.nudgeDocumentRequest(token, r.id);
                    toast.success('Reminder sent');
                    onUpdate();
                  } : null}
                  onComplete={r.status !== 'fulfilled' ? async () => {
                    await portalAdminApi.patchDocumentRequest(token, r.id, { status: 'fulfilled' });
                    toast.success('Marked fulfilled');
                    onUpdate();
                  } : null}
                  onReopen={r.status === 'fulfilled' ? async () => {
                    await portalAdminApi.patchDocumentRequest(token, r.id, { status: 'open' });
                    onUpdate();
                  } : null}
                  onDelete={async () => {
                    await portalAdminApi.deleteDocumentRequest(token, r.id);
                    toast.success('Removed');
                    onUpdate();
                  }}
                  testIdSuffix={`doc-${r.id}`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RowActions({ onNudge, onComplete, onReopen, onDelete, testIdSuffix }) {
  const [confirming, setConfirming] = useState(false);
  const [nudging, setNudging] = useState(false);
  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Button size="sm" variant="destructive" onClick={async () => { await onDelete(); setConfirming(false); }} data-testid={`row-delete-confirm-${testIdSuffix}`}>
          Confirm delete
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {onNudge && (
        <Button
          size="sm"
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          disabled={nudging}
          onClick={async () => { setNudging(true); try { await onNudge(); } finally { setNudging(false); } }}
          data-testid={`row-nudge-${testIdSuffix}`}
        >
          {nudging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><BellRing className="w-3.5 h-3.5 mr-1" /> Nudge</>}
        </Button>
      )}
      {onComplete && (
        <Button size="sm" variant="outline" onClick={onComplete} data-testid={`row-complete-${testIdSuffix}`}>
          Mark done
        </Button>
      )}
      {onReopen && (
        <Button size="sm" variant="outline" onClick={onReopen} data-testid={`row-reopen-${testIdSuffix}`}>
          Reopen
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => setConfirming(true)} data-testid={`row-delete-${testIdSuffix}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </span>
  );
}

function AuditTable({ events }) {
  if (events.length === 0) {
    return <p className="text-sm text-[#8C8078] text-center py-8">No audit events logged for this client yet.</p>;
  }
  return (
    <table className="w-full text-sm" data-testid="portal-audit-table">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-[#8C8078]">
          <th className="pb-2 font-medium">When</th>
          <th className="pb-2 font-medium">Who</th>
          <th className="pb-2 font-medium">Action</th>
          <th className="pb-2 font-medium">Outcome</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#F0EAE3]">
        {events.map((e, idx) => (
          <tr key={e.id || idx} data-testid="portal-audit-row">
            <td className="py-2.5 pr-3 text-[#5D524D] whitespace-nowrap">{fmtDate(e.timestamp, "d MMM, h:mm a")}</td>
            <td className="py-2.5 pr-3 text-[#2C2420]">
              <p className="font-medium">{e.actorName || '—'}</p>
              <p className="text-xs text-[#8C8078]">{e.actorEmail}</p>
            </td>
            <td className="py-2.5 pr-3 text-[#5D524D]">
              <span className="capitalize">{(e.action || '').replace(/_/g, ' ')}</span>
              {e.object && <span className="text-[#8C8078]"> · {e.object}</span>}
            </td>
            <td className="py-2.5 text-[#5D524D]">{e.outcome || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Dialogs
// ──────────────────────────────────────────────────────────────────────────

function CreateActionItemDialog({ user, token, clientId, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    setSaving(true);
    try {
      await portalAdminApi.createActionItem(token, clientId, {
        portalUserId: user.id,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate: dueDate ? new Date(`${dueDate}T00:00:00Z`).toISOString() : undefined,
      });
      toast.success(`Action item sent to ${user.name}`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create action item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="create-action-item-dialog">
        <DialogHeader>
          <DialogTitle>New action item</DialogTitle>
          <DialogDescription>
            Send {user.name} a task. They'll get an email and see it in their portal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Review and approve estate summary" data-testid="action-item-title-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Description (optional)</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Add any context they'll need…" data-testid="action-item-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Priority</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="action-item-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Standard</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Due date (optional)</label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="action-item-due-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2E26]" data-testid="action-item-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send action item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDocumentRequestDialog({ user, token, clientId, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a document name');
      return;
    }
    setSaving(true);
    try {
      await portalAdminApi.createDocumentRequest(token, clientId, {
        portalUserId: user.id,
        title: title.trim(),
        description: description.trim() || undefined,
        dueDate: dueDate ? new Date(`${dueDate}T00:00:00Z`).toISOString() : undefined,
      });
      toast.success(`Request sent to ${user.name}`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create document request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="create-doc-request-dialog">
        <DialogHeader>
          <DialogTitle>Request a document</DialogTitle>
          <DialogDescription>
            Ask {user.name} to upload a specific document. They'll get an email with a deep link.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Document name</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Updated passport (front + back)" data-testid="doc-request-title-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Notes (optional)</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Any specifics they should know…" data-testid="doc-request-description-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Due date (optional)</label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="doc-request-due-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2E26]" data-testid="doc-request-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserMetaDialog({ user, token, onClose, onSaved }) {
  const isAdviser = user.portalRole === 'external_adviser';
  const [welcomeNote, setWelcomeNote] = useState(user.customWelcomeNote || '');
  const [matterScope, setMatterScope] = useState(user.matterScope || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { customWelcomeNote: welcomeNote };
      if (isAdviser) body.matterScope = matterScope;
      await portalAdminApi.patchPortalUser(token, user.id, body);
      toast.success('Updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="edit-user-meta-dialog">
        <DialogHeader>
          <DialogTitle>Welcome message {isAdviser && '& matter scope'}</DialogTitle>
          <DialogDescription>
            Customise what {user.name} sees the moment they sign in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Welcome note</label>
            <Textarea
              value={welcomeNote}
              onChange={(e) => setWelcomeNote(e.target.value)}
              rows={3}
              placeholder="e.g. Welcome, John. This is your private portal — please reach out if anything is unclear."
              data-testid="welcome-note-input"
            />
            <p className="text-xs text-[#8C8078] mt-1.5">Appears in the dark hero on their portal home.</p>
          </div>
          {isAdviser && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">Matter scope</label>
              <Textarea
                value={matterScope}
                onChange={(e) => setMatterScope(e.target.value)}
                rows={4}
                placeholder="e.g. Estate planning and trust administration for the Whitfield family."
                data-testid="matter-scope-input"
              />
              <p className="text-xs text-[#8C8078] mt-1.5">Tells them exactly what remit you've granted.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2E26]" data-testid="edit-user-meta-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
